import { useContext } from 'react';
import { ActionContext } from 'src/contexts/action-context';
import {
  AttributeContext,
  AttributeEditorContext,
  GenericAttributeEditorContext
} from 'src/contexts/attribute-context';
import { FirebaseContext } from 'src/contexts/auth/firebase-context';
import { KeycloakContext } from 'src/contexts/auth/keycloak-context';
import { OidcContext } from 'src/contexts/auth/oidc-context';
import { CloudTokenContext } from 'src/contexts/cloud-token-context';
import { GetStreamContext } from 'src/contexts/getstream/getstream-context';
import {
  ItemStoreContext,
  ItemStoreContextBase,
  ItemStoreContextTypes
} from 'src/contexts/item-stores/item-store-context';
import { MeContext } from 'src/contexts/me-context';
import { NotificationsContext } from 'src/contexts/notifications/notifications-context';
import { PaginatedStepperContext } from 'src/contexts/paginated-stepper-context';
import { SettingsContext } from 'src/contexts/settings-context';
import {
  TableContext,
  TableContextType,
  TableContextTypes
} from 'src/contexts/tables/table-context';
import { TenantContext } from 'src/contexts/tenant-context';
import { TableCrudActions } from 'src/hocs/with-table-store';
import { AttributeFormat } from 'src/types/attributes';
import { TableStore } from 'src/types/hooks';
import { InvalidContextError } from 'src/utils/errors';

export type ValidateContextOptions<Optional extends boolean = false> = {
  optional?: Optional;
};

export type InferReturnType<T, Optional> = Optional extends true
  ? T | undefined
  : T;

const validateContext = <T, Optional extends boolean = false>(
  context: T | undefined,
  options?: ValidateContextOptions<Optional>
): InferReturnType<T, Optional> => {
  if (!context && !options?.optional)
    throw new InvalidContextError('Context is undefined');
  return context as InferReturnType<T, Optional>;
};

const extractContext = <T, K>(
  context: T,
  extract: (context: T) => Partial<K>
): K & { context: T } => {
  const extraction = extract(context);

  // ensure the extracted properties are valid
  const missing = Object.entries(extraction)
    .filter(([_, value]) => value === undefined)
    .map(([key]) => [key]);

  if (missing.length)
    throw new InvalidContextError(`Context must provide ${missing.join(', ')}`);

  return { ...(extraction as K), context };
};

export const useActionContext = () => {
  return validateContext(useContext(ActionContext));
};
export const useAttributeContext = () => {
  return validateContext(useContext(AttributeContext));
};
export const useSettingsContext = () => {
  return validateContext(useContext(SettingsContext));
};
export const useCloudTokenContext = () => {
  return validateContext(useContext(CloudTokenContext));
};
export const useFirebaseContext = () => {
  return validateContext(useContext(FirebaseContext));
};
export const useKeycloakContext = () => {
  return validateContext(useContext(KeycloakContext));
};
export const useOidcContext = () => {
  return validateContext(useContext(OidcContext));
};
export const useGetStreamContext = () => {
  return validateContext(useContext(GetStreamContext));
};
export const useMeContext = () => {
  return validateContext(useContext(MeContext));
};
export const useNotificationsContext = () => {
  return validateContext(useContext(NotificationsContext));
};
export const useTenantContext = () => {
  return validateContext(useContext(TenantContext));
};
export const usePaginatedStepperContext = () => {
  return validateContext(useContext(PaginatedStepperContext));
};

// select the correct auth context hook
const getAuthContextHook = () => {
  const authProvider = globalThis.__TENANT__?.authProvider;

  if (authProvider === 'keycloak') return useKeycloakContext;
  if (authProvider === 'firebase') return useFirebaseContext;
  if (authProvider === 'oidc') return useOidcContext;
  throw new Error('Invalid auth provider');
};

export const useAuthContext = getAuthContextHook();

const validateTableStoreContext = <T extends keyof TableContextTypes & string>(
  context: TableContextType<string, TableStore<object>, unknown, unknown>,
  type: T
) => {
  if (context.type !== type)
    throw new InvalidContextError(`Expected ${type} (got ${context.type})`);
  return context as TableContextTypes[T];
};

export function useTableContext<T extends keyof TableContextTypes & string>(
  type: T
): TableContextTypes[T];
// untyped table store access (use typing with caution)
export function useTableContext<
  T extends object = object,
  Filters = object
>(): TableContextType<string, TableStore<T>, unknown, unknown, Filters>;

export function useTableContext<T extends keyof TableContextTypes & string>(
  type?: T
):
  | TableContextType<string, TableStore<object>, unknown, unknown>
  | TableContextTypes[T] {
  const context = validateContext(useContext(TableContext));
  return type === undefined
    ? context
    : validateTableStoreContext<T>(context, type);
}

const validateItemStoreContext = <
  T extends keyof ItemStoreContextTypes & string,
  Optional extends boolean = false
>(
  context: ItemStoreContextBase | undefined,
  type: T,
  options?: ValidateContextOptions<Optional>
): InferReturnType<ItemStoreContextTypes[T], Optional> => {
  if (context?.type !== type && !options?.optional)
    throw new InvalidContextError(`Expected ${type} (got ${context?.type})`);

  return (context?.type === type ? context : undefined) as InferReturnType<
    ItemStoreContextTypes[T],
    Optional
  >;
};

export const useItemStoreContext = <
  T extends keyof ItemStoreContextTypes & string,
  Optional extends boolean = false
>(
  type: T,
  options?: ValidateContextOptions<Optional>
): InferReturnType<ItemStoreContextTypes[T], Optional> => {
  const context = validateContext(useContext(ItemStoreContext), options);
  return validateItemStoreContext<T, Optional>(context, type, options);
};

// code-sugar for crud tables
export const useTableCrudActions = <Item = unknown, Add = unknown>() => {
  const context = useTableContext();
  // return full partial for type safety
  return context.extra as Partial<TableCrudActions<Item, Add>>;
};

// code-sugar for post editor
export const usePostEditor = () =>
  extractContext(
    useItemStoreContext('post'),
    ({
      api: { handlePatchItem },
      store: { item },
      extra: { handleNavigateParent }
    }) => ({ item, handlePatchItem, handleNavigateParent })
  );

export function useAttributeEditor(
  format: 'matrix'
): GenericAttributeEditorContext<'matrix'>;
export function useAttributeEditor(
  format: 'value'
): GenericAttributeEditorContext<'value'>;
export function useAttributeEditor(
  format: AttributeFormat
): AttributeEditorContext {
  const { editor } = validateContext(useContext(AttributeContext));
  if (editor?.format !== format)
    throw new InvalidContextError(
      `Expected attribute format ${format} (got ${editor?.format})`
    );

  return editor;
}

export const useMe = () => {
  const context = useMeContext();
  if (context.profile === undefined) throw new Error('Profile is undefined');
  return context.profile;
};
